import { useState } from 'react';
import { Dropdown, TextInput } from '@pmi/dsm-react';
import { RemoveSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import { Option } from '@pmi/dsm-react/dist/components/menus/types';
import { useFormikContext } from 'formik';
import { Form } from 'react-bootstrap';
import { PhoneNumberInput, SelectOption } from './models';
import styles from './phoneNumber.module.scss';

export default function PhoneNumberForm({
  countries,
  title = 'Phone Number',
  showExtension = true,
}: {
  countries: SelectOption[];
  title?: string;
  showExtension?: boolean;
}) {
  const formik = useFormikContext<PhoneNumberInput>();
  const countryRegionCodeOptions: Option[] =
    countries?.map((pn) => ({
      label: pn.text,
      value: pn.value,
    })) ?? [];
  const [options, setOptions] = useState(countryRegionCodeOptions);

  const errorMsg = (msg: string) => (
    <div className="invalid-feedback">
      <div className="d-flex">
        <span className="d-block me-1">
          <RemoveSolidIcon fontSize="sm" />
        </span>
        <span className="d-block">{msg}</span>
      </div>
    </div>
  );

  const handleInputChange = (_: any, value: string) => {
    const newOptions = countryRegionCodeOptions.filter((option) =>
      option.label.toLocaleLowerCase().startsWith(value.toLocaleLowerCase()),
    );
    setOptions(newOptions);
  };

  return (
    <>
      <h4 className="my-2">{title}</h4>
      <div className="row">
        <div
          className={
            showExtension ? 'col-6 col-md-5 mb-3' : 'col-12 col-md-6 mb-3'
          }
        >
          <Form.Group
            controlId="phoneNumber.countryId"
            className={
              formik.errors?.phoneNumber?.countryId ? 'is-invalid' : null
            }
          >
            <Form.Label>
              Country/Region Code <span className="text-danger">*</span>
            </Form.Label>
            <Dropdown
              className={styles.dropdownContainer}
              onChange={(e, value) => {
                formik.setFieldTouched('phoneNumber.countryId', true);
                formik.setFieldValue('phoneNumber.countryId', Number(value));
                setOptions(countryRegionCodeOptions);
              }}
              onChangeInput={handleInputChange}
              options={options}
              invalid={!!formik.errors.phoneNumber?.countryId}
              placeholder="Select Country / Region"
              value={formik.values.phoneNumber?.countryId?.toString()}
            />
            {formik.errors.phoneNumber?.countryId
              ? errorMsg('Please enter the Country/Region Code')
              : null}
          </Form.Group>
        </div>

        <div
          className={
            showExtension ? 'col-6 col-md-5 mb-3' : 'col-12 col-md-6 mb-3'
          }
        >
          <Form.Group
            controlId="number"
            className={formik.errors?.phoneNumber?.number ? 'is-invalid' : null}
          >
            <Form.Label>
              Number <span className="text-danger">*</span>
            </Form.Label>
            <TextInput
              name="phoneNumber.number"
              onChange={formik.handleChange}
              value={formik.values?.phoneNumber?.number}
              isInvalid={!!formik.errors?.phoneNumber?.number}
              placeholder="Phone Number"
              className={
                formik.errors?.phoneNumber?.number
                  ? 'is-invalid'
                  : styles.inputBorder
              }
            />
            {formik.errors.phoneNumber?.number
              ? errorMsg('Please enter the Phone Number')
              : null}
          </Form.Group>
        </div>

        {showExtension && (
          <div className="col-6 col-md-2 mb-3">
            <Form.Group
              controlId="phoneNumber.extension"
              className={
                formik.errors?.phoneNumber?.extension ? 'is-invalid' : null
              }
            >
              <Form.Label>Ext</Form.Label>
              <TextInput
                name="phoneNumber.extension"
                onChange={formik.handleChange}
                placeholder="Ext"
                value={formik.values?.phoneNumber?.extension}
                className={
                  formik.errors?.phoneNumber?.extension
                    ? 'is-invalid'
                    : styles.inputBorder
                }
                isInvalid={!!formik.errors?.phoneNumber?.extension}
              />
              {formik.errors.phoneNumber?.number
                ? errorMsg(formik.errors?.phoneNumber?.extension)
                : null}
            </Form.Group>
          </div>
        )}
        <div className="row mb-3">
          <div className="col-12">
            <span className="text-danger">*</span> These fields are required.
          </div>
        </div>
      </div>
    </>
  );
}
